import { AlertColor, FormControlLabel, Switch } from '@mui/material';
import { CountryCode } from 'libphonenumber-js';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import strings from '../../../common/Translation/Translate';
import Notification from '../../../components/Notification';
import { FormDataModel } from '../../../components/Form';
import axiosInstance from '../../../utils/axios';
import { privileges } from '../../../utils/constants/privileges';
import { URLS } from '../../../utils/constants/urls';
import { fetchFromStorage } from '../../../utils/storage';
import { createDropdownData, fetchIdsFromSelectedDropdownValues } from '../../../utils/validators/HelperFunctions';
import { useSelector } from 'react-redux';
import { Privilege } from 'utils/redux/reducer/authentication-slice';
import MasterDataEntityDrawer from '../CommonPage/MasterDataEntityDrawer';
import Delete from '../CommonPage/Delete';
import { AddUserForm } from '../AddUser/AddUserForm';
import { getRoles, getUserOrg } from '../AddUser/userService';

const defaultFormValues = (defaultLanguage?: { value: any; label: any }): FormDataModel => ({
  title: null,
  firstName: '',
  lastName: '',
  email: '',
  phone: {},
  roles: '',
  designation: '',
  language: defaultLanguage ?? '',
  organizations: '',
  reportingTo: '',
  address: '',
  country: '',
  state: '',
  city: '',
  pinCode: '',
  enabled: true,
});

interface UsersEntityDrawerProps {
  open: boolean;
  editingId: number | null;
  onClose: () => void;
  onSaved: () => void;
}

const UsersEntityDrawer: FC<UsersEntityDrawerProps> = ({ open, editingId, onClose, onSaved }) => {
  const defaultCountry = (fetchFromStorage('defaultCountryCode') ?? 'IN') as CountryCode;
  const userPrivileges = useSelector(Privilege) as string[];
  const isEdit = Boolean(editingId);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteEntityName, setDeleteEntityName] = useState('');
  const [deleteUserId, setDeleteUserId] = useState<number | null>(null);
  const [formValues, setFormValues] = useState<FormDataModel>(defaultFormValues());
  const [shouldAutoFocus, setShouldAutoFocus] = useState(false);
  const [key, setKey] = useState(0);

  const [roles, setRoles] = useState<any[]>([]);
  const [languages, setLanguages] = useState<any[]>([]);
  const [countries, setCountries] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [assignees, setAssignees] = useState<any[]>([]);
  const [salutations, setSalutations] = useState<any[]>([]);

  const [message, setMessage] = useState<{ display: boolean; severity: AlertColor | null; message: string }>({
    display: false,
    severity: null,
    message: '',
  });

  const closeNotification = (value: boolean) => setMessage(prev => ({ ...prev, display: value }));

  const handleCatch = (error: any) => {
    setMessage({
      display: true,
      severity: 'error',
      message: error?.response?.data?.error_description ?? error?.response?.data?.message ?? error?.message,
    });
  };

  const getCountry = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(URLS.countries);
      setCountries(data.map((d: any) => ({ label: d.name, value: d.id })));
    } catch (error: any) {
      handleCatch(error);
    }
  }, []);

  const getState = useCallback(async (countryId: number) => {
    try {
      const { data } = await axiosInstance.get(URLS.states(countryId));
      setStates(data.map((d: any) => ({ label: d.name, value: d.id })));
    } catch (error: any) {
      handleCatch(error);
    }
  }, []);

  const getCities = useCallback(async (stateId: number) => {
    try {
      const { data } = await axiosInstance.get(URLS.cities(stateId));
      setCities(data.map((d: any) => ({ label: d.name, value: d.id })));
    } catch (error: any) {
      handleCatch(error);
    }
  }, []);

  const getLanguage = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(URLS.language);
      setLanguages(data.map((d: any) => ({ name: d.text, id: d.text })));
    } catch (error: any) {
      handleCatch(error);
    }
  }, []);

  const getMasterData = useCallback(async (currentId: number | null) => {
    try {
      const { data } = await axiosInstance.get(URLS.followUpDropdown);
      if (currentId) {
        setAssignees(
          (data?.assignees || [])
            .filter((u: any) => u.id !== Number(currentId))
            .map((u: any) => ({ label: u.name, value: u.id })),
        );
      } else {
        setAssignees(createDropdownData(data?.assignees, ['id', 'name']));
      }
      const contactTitles = await axiosInstance(URLS.getSalutations);
      setSalutations(createDropdownData(contactTitles.data, ['id', 'name']));
    } catch (error: any) {
      handleCatch(error);
    }
  }, []);

  const getUserRoles = useCallback(async () => {
    try {
      const data = await getRoles();
      setRoles((data || []).map((item: any) => ({ label: item.name, value: item.name })));
    } catch (error: any) {
      handleCatch(error);
    }
  }, []);

  const getOrganizations = useCallback(async () => {
    try {
      const data = await getUserOrg();
      setOrganizations(data || []);
    } catch (error: any) {
      handleCatch(error);
    }
  }, []);

  useEffect(() => {
    getCountry();
    getLanguage();
    getUserRoles();
    getOrganizations();
  }, [getCountry, getLanguage, getUserRoles, getOrganizations]);

  const loadUser = useCallback(
    async (id: number) => {
      try {
        setLoading(true);
        const { data } = await axiosInstance.get(URLS.getUserById(id));
        if (data?.country?.value) await getState(data.country.value);
        if (data?.state?.value) await getCities(data.state.value);
        setFormValues({
          ...data,
          roles: data.role.value,
          title: data?.title ? { value: data.title, label: data.title } : null,
          city: data?.city?.value,
          state: data?.state?.value,
          country: data?.country?.value,
        });
      } catch (error: any) {
        handleCatch(error);
      } finally {
        setLoading(false);
      }
    },
    [getState, getCities],
  );

  useEffect(() => {
    if (!open) return;
    getMasterData(editingId);
    setStates([]);
    setCities([]);
    setKey(prev => prev + 1); // Force re-render
    setShouldAutoFocus(!editingId); // One-time autofocus for new users only
    if (editingId) {
      loadUser(editingId);
    } else {
      setFormValues(defaultFormValues(createDropdownData(languages, ['id', 'name'])[0]));
    }
    // Clear autofocus after first paint so country/state reloads don't jump focus to firstName
    const clearAutoFocusTimer = window.setTimeout(() => setShouldAutoFocus(false), 400);
    return () => window.clearTimeout(clearAutoFocusTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingId]);

  const formModel = useMemo(
    () => AddUserForm(salutations, roles, countries, states, cities, assignees, defaultCountry, shouldAutoFocus),
    [salutations, roles, countries, states, cities, assignees, defaultCountry, shouldAutoFocus],
  );

  const dropdownData = useMemo(
    () => ({
      organizations: createDropdownData(organizations, ['id', 'name']),
      language: createDropdownData(languages, ['id', 'name']),
    }),
    [organizations, languages],
  );

  /** Restore focus on the field the user just changed (don't jump to top / next field). */
  const keepFocusOnField = (field: string) => {
    window.setTimeout(() => {
      const root = document.getElementById(field);
      const input =
        (root?.querySelector?.('input') as HTMLInputElement | null) ||
        (root as HTMLInputElement | null);
      input?.focus?.();
    }, 50);
  };

  const handleFieldChange = async (field: string, value: any, formData: FormDataModel) => {
    if (field === 'country') {
      setShouldAutoFocus(false);
      setCities([]);
      setFormValues({ ...formData, state: '', city: '', country: value });
      await getState(value);
      keepFocusOnField('country');
    } else if (field === 'state') {
      setShouldAutoFocus(false);
      setFormValues({ ...formData, city: '', state: value });
      await getCities(value);
      keepFocusOnField('state');
    }
  };

  const handleSave = async (body: FormDataModel) => {
    try {
      setSaving(true);
      const city = cities.find((el: any) => el.value === body.city);
      const country = countries.find((el: any) => el.value === body.country);
      const state = states.find((el: any) => el.value === body.state);
      const payload = {
        ...body,
        id: editingId ?? (formValues as any)?.id,
        city: city?.value ?? body.city,
        state: state?.value ?? body.state,
        country: country?.value ?? body.country,
        title: (body.title as any)?.label,
        language: (body as any)?.language?.label ?? '',
        role: body.roles,
        pinCode: body?.pinCode?.toString().trim() === '' ? null : body?.pinCode,
        reportingTo: fetchIdsFromSelectedDropdownValues(body?.reportingTo as any) || [],
        organizations: fetchIdsFromSelectedDropdownValues(body?.organizations as any) || [],
      };
      if (editingId) {
        await axiosInstance.put(URLS.editUserById(String(editingId)), payload);
      } else {
        await axiosInstance.post(URLS.addUser, payload);
      }
      setMessage({
        display: true,
        severity: 'success',
        message: editingId ? strings.common_update_message : strings.common_save_message,
      });
      onSaved();
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setSaving(false);
    }
  };

  const openUserDelete = (entityName: string) => {
    if (!editingId) return;
    setDeleteEntityName(entityName);
    setDeleteUserId(editingId);
    setDeleteOpen(true);
  };

  const closeUserDelete = () => {
    setDeleteOpen(false);
    setDeleteEntityName('');
    setDeleteUserId(null);
  };

  const handleToggleEnabled = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!editingId) return;
    const newEnabledValue = event.target.checked;
    try {
      setSaving(true);
      await axiosInstance.put(URLS.toggleUser(editingId));
      setFormValues(prev => ({ ...prev, enabled: newEnabledValue }));
      setMessage({
        display: true,
        severity: 'success',
        message: strings.status_change_success_message,
      });
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setSaving(false);
    }
  };

  const headerExtra = isEdit ? (
    <FormControlLabel
      control={
        <Switch
          checked={Boolean(formValues.enabled)}
          onChange={handleToggleEnabled}
          disabled={saving}
          color="primary"
        />
      }
      label={formValues.enabled ? strings.usermgmt_button_active_user : strings.usermgmt_button_in_active_user}
    />
  ) : null;

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}
      <MasterDataEntityDrawer
        key={key}
        open={open}
        title={isEdit ? strings.edit_user_text : strings.usermgmt_title_add}
        isEdit={isEdit}
        formModel={formModel}
        formValues={formValues}
        dropdownData={dropdownData}
        onFieldChange={handleFieldChange}
        headerExtra={headerExtra}
        loading={loading}
        saving={saving}
        canDelete={userPrivileges?.includes(privileges.delete_user)}
        width="60%"
        onClose={onClose}
        onSave={handleSave}
        onRequestDelete={openUserDelete}
        deleteEntityName={
          `${formValues.firstName || ''} ${formValues.lastName || ''}`.trim() || String(formValues.email || '')
        }
        onInvalid={msg => setMessage({ display: true, severity: 'error', message: msg })}
      />
      {deleteUserId ? (
        <Delete
          page="user"
          openModal={deleteOpen}
          closeModal={closeUserDelete}
          deleteUrl={URLS.deleteUserById(String(deleteUserId))}
          entityName={deleteEntityName}
          updateList={onSaved}
        />
      ) : null}
    </>
  );
};

export default UsersEntityDrawer;
